import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../contexts/auth-context";
import moment from "moment";
import { getQueuesAPI } from "../util";
import { getGatesAPI } from "./Gates/dummyLiveData";
import { getFacilitiesAPI } from "./Facilities/dummyLiveData";
import { getRetailAPI } from "./Retail/dummyLiveData";
import { getConcessionAPI } from "./Concession/dummyLiveData";
import changes from "../changes.json";

// How often the cards' live numbers refresh
const LIVE_REFRESH_MS = 60 * 1000;

const LocationsController = ({ children }) => {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  // Retail acts as our static information of a retail location from backend
  const [retail, setRetail] = useState([]);
  const [originalRetail, setOriginalRetail] = useState([]);
  // Concessions acts as our static information of a retail location from backend
  const [concessions, setConcessions] = useState([]);
  const [originalConcessions, setOriginalConcessions] = useState([]);
  // Facilities acts as our static information of a facilities location from backend
  const [facilities, setFacilities] = useState([]);
  const [originalFacilities, setOriginalFacilities] = useState([]);

  // Gates acts as our static info of a gate location from backend
  const [gates, setGates] = useState([]);
  const [originalGates, setOriginalGates] = useState([]);

  // Retail Location Data acts as our live/current information of a retail location
  // In our Locations.js we merge the static and live information to send/display on a card
  const [retailLocationData, setRetailLocationData] = useState({});  // Store location data per location
  // Concession Location Data acts as our live/current information of a concession location
  // In our Locations.js we merge the static and live information to send/display on a card
  const [concessionLocationData, setConcessionLocationData] = useState({});  // Store location data per location
  // Facilities Location Data acts as our live/current information of a facilities location
  // In our Locations.js we merge the static and live information to send/display on a card
  const [facilitiesLocationData, setFacilitiesLocationData] = useState({});  // Store location data per location

  const [gatesLocationData, setGatesLocationData] = useState({});

  const [currentSearchTerm, setCurrentSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState('');
  const [showOutageAlert, setShowOutageAlert] = useState(false);
  const [showFoodSection, setShowFoodSection] = useState(true);
  const [showBeverageSection, setShowBeverageSection] = useState(true);
  const [showRetailSection, setShowRetailSection] = useState(true);
  const [showFacilitiesSection, setShowFacilitiesSection] = useState(true);
  const [showGatesSection, setShowGatesSection] = useState(true);
  const [foodItems, setFoodItems] = useState([]);
  const [beverageItems, setBeverageItems] = useState([]);

  const handleDismiss = () => setShowOutageAlert(false);

  useEffect(() => {
    // Only fetch data if logged in
    // Prompt login if user is not logged in
    // One of main entry points
    if (authCtx.loggedIn) {
      fetchData();
    } else {
      navigate('/auth');
    }
  }, []);

  // Refresh the cards' live numbers every minute (quietly: no spinner, search and sort kept).
  // The header shows when the cards last updated and counts down to the next refresh.
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [nextRefreshAt, setNextRefreshAt] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Bumped after every load or refresh, which restarts the countdown
  const [refreshCycle, setRefreshCycle] = useState(0);

  const refreshLiveData = async () => {
    if (!authCtx.authLocationData) return;
    setIsRefreshing(true);
    try {
      await fetchLiveData(authCtx.authLocationData, true);
      setLastUpdatedAt(Date.now());
    } catch (e) {
      // Keep the cards' previous numbers; the next refresh tries again
    } finally {
      setIsRefreshing(false);
      setRefreshCycle((c) => c + 1);
    }
  };

  useEffect(() => {
    if (!authCtx.loggedIn || !authCtx.authLocationData || isLoading) return undefined;
    setNextRefreshAt(Date.now() + LIVE_REFRESH_MS);
    const timer = setTimeout(refreshLiveData, LIVE_REFRESH_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authCtx.loggedIn, authCtx.authLocationData, authCtx.token, isLoading, refreshCycle]);

  useEffect(() => {
    setFoodItems(concessions.filter(item => item.type === 'food'));
    setBeverageItems(concessions.filter(item => item.type === 'beverage'));
  }, [concessions]);

  useEffect(() => {
    setShowFoodSection(foodItems.length > 0);
    setShowBeverageSection(beverageItems.length > 0);
  }, [foodItems]);

  useEffect(() => {
    setShowRetailSection(retail.length > 0);
  }, [retail]);

  useEffect(() => {
    setShowFacilitiesSection(facilities.length > 0);
  }, [facilities]);

  useEffect(() => {
    setShowGatesSection(gates.length > 0);
  }, [gates]);

  // We support 3 types, Queue Time, Door Counts, Occupancy
  // We want to show the types in the priority above with Occupancy having highest priority
  // WE NEED TO UPDATE THIS TO BE PEOPLE COUNT NOW QUEUE TIME TYPE
  const fetchRetailLocationData = (location) => {
    // Unique retail location that only has door counting (max occupancy is 0 meaning no zone tracking)
    // If location does have occupancy we need to show that foremost
    if (location.maxOccupancy === 0) {
      // Wait this location has no door counters 
      // and has queue time sensors so it must be queue time location
      if (location.doorCounterSensorIds.length === 0 && location.queueTimeSensorIds.length != 0) {
      // Hard check above soft one rn for testing
        // console.log('We are a queue time retail location')
        return new Promise((resolve) => {
          // Should be people count type not queue-time since we can only determine a queue if we
          // have zones/occupancy set up (maxOccupancy != 0)
          // Thus this route returns queue time and people count but we will only ever pull people count
          // and display it in the card cus, if they have counts we rather show counts
          // and if they have occupancy we rather show occupancy
          getRetailAPI(location)("get-retail-location-current-queue-time",
          {
            measurement: location.measurement || "",
            queueMeasurement: location.queueMeasurement,
            locationId: location.id,
            venue: authCtx.venue,
            // timezone: location.timeZone,
            queueTimeSensorIds: location.queueTimeSensorIds,
          },
          (data, error) => {
            if (error) {
              if (error.status === 401) {
                authCtx.logout();
                navigate("/auth");
                resolve({ error: "unauthorized" });
              } else {
                resolve({ error: "failed" });
              }
            } else if (data) {
              resolve({
                locationId: location.id,
                type: 'retail',
                data: {
                  type: 'line count',
                  lineCount: data.peopleCount,
                  queueTime: data.queueTime
                }
              });
            } else {
              resolve({ error: "invalid" });
            }
          },
          authCtx.token
        );
        });
      }
      // Even if we do support queue time, we have count information so lets show that 
      else {
        return new Promise((resolve) => {
          // Combine sales and stairs sensor IDs for exclusion
          const excludedSensorIds = [
            ...(location.doorCounterSensorSalesIds || []),
            ...(location.doorCounterSensorStairsIds || [])
          ];

          // Filter out excluded sensors
          const filteredDoorCountSensorIds = location.doorCounterSensorIds.filter(
            id => !excludedSensorIds.includes(id)
          );
          getRetailAPI(location)("get-retail-location-current-count-data-dashboard",
          {
            measurement: location.measurement || "",
            doorMeasurement: location.doorMeasurement,
            queueMeasurement: location.queueMeasurement,
            locationId: location.id,
            venue: authCtx.venue,
            zones: location.zones.map(zone => zone.id),
            totalOccupancySensors: location.totalOccupancySensors,
            timezone: location.timeZone,
            doorCountSensorIds: filteredDoorCountSensorIds, //location.doorCounterSensorIds,
          },
          (data, error) => {
            if (error) {
              if (error.status === 401) {
                authCtx.logout();
                navigate("/auth");
                resolve({ error: "unauthorized" });
              } else {
                resolve({ error: "failed" });
              }
            } else if (data) {
              resolve({
                locationId: location.id,
                type: 'retail',
                data: {
                  type: 'door count',
                  doorCount: data.currentDoorCount
                }
              });
            } else {
              resolve({ error: "invalid" });
            }
          },
          authCtx.token
        );
        });
      }
    } 
    // Regular retail location with occupancy tracking
    // For retail locations with occupancy tracking (maxOccupancy > 0)
    // UPDATED TO V2
    const occupancyRequest = new Promise((resolve) => {
      getRetailAPI(location)(
        "get-retail-location-current-data-dashboard-v2",  // <-- Changed to v2
        {
          zones: location.zones,  // <-- Pass full zone config (not zone IDs)
          venue: authCtx.venue,
          locationId: location.id,
          measurement: location.measurement || ""
        },
        (data, error) => {
          if (error) {
            if (error.status === 401) {
              authCtx.logout();
              navigate("/auth");
              resolve({ error: "unauthorized" });
            } else {
              resolve({ error: "failed" });
            }
          } else if (data) {
            const activeCount = data.filter(sensor => sensor.sensorActive).length;
            const totalCount = data.length;
            const isOutage = activeCount / totalCount <= 0.5;
            const current = data.reduce((x, y) => x + y.occupancy, 0);
            const percentage = Math.round((current / location.maxOccupancy) * 100);

            resolve({
              locationId: location.id,
              type: 'retail',
              data: {
                type: 'occupancy',
                showOutage: isOutage,
                current,
                percentage
              }
            });
          } else {
            resolve({ error: "invalid" });
          }
        },
        authCtx.token
      );
    });

    // Occupancy stores with entrance counters also show traffic today on the card
    if (!(location.doorCounterSensorIds || []).length) return occupancyRequest;
    return Promise.all([occupancyRequest, fetchRetailTrafficToday(location)]).then(([result, doorCount]) => (
      result.data && doorCount != null
        ? { ...result, data: { ...result.data, doorCount } }
        : result
    ));
  };

  // Traffic today from the entrance counters (sales and stairs counters excluded), same as count-only stores.
  // Resolves null on any failure so the occupancy card still shows; the occupancy request handles 401s.
  const fetchRetailTrafficToday = (location) => {
    const excludedSensorIds = [
      ...(location.doorCounterSensorSalesIds || []),
      ...(location.doorCounterSensorStairsIds || [])
    ];
    const entranceSensorIds = (location.doorCounterSensorIds || []).filter(id => !excludedSensorIds.includes(id));
    if (!entranceSensorIds.length) return Promise.resolve(null);
    return new Promise((resolve) => {
      getRetailAPI(location)("get-retail-location-current-count-data-dashboard",
      {
        measurement: location.measurement || "",
        doorMeasurement: location.doorMeasurement,
        queueMeasurement: location.queueMeasurement,
        locationId: location.id,
        venue: authCtx.venue,
        zones: location.zones.map(zone => zone.id),
        totalOccupancySensors: location.totalOccupancySensors,
        timezone: location.timeZone,
        doorCountSensorIds: entranceSensorIds,
      },
      (data, error) => {
        const count = !error && data ? Number(data.currentDoorCount) : NaN;
        resolve(Number.isFinite(count) ? count : null);
      },
      authCtx.token
      );
    });
  };

  // Uses route to only get the most recent data for a concession location (current data)
  const fetchConcessionLocationData = (location) => {
    // Use old route if venue is braves or royals for concessions
    // Otherwise use the v2 route
    if (authCtx.venue == "royals") {
      return new Promise((resolve) => {
        getConcessionAPI(location)(
          "get-concession-location-current-data-dashboard",
          {
            sections: location.sections.map(section => section.sectionId),
            venue: authCtx.venue, // braves
            locationId: location.id, // 2 (concession)
            measurement: location.measurement || "" // clubhouse
          },
          (data, error) => {
            if (error) {
              if (error.status === 401) {
                authCtx.logout();
                navigate("/auth");
                resolve({ error: "unauthorized" });
              } else {
                resolve({ error: "failed" });
              }
            } else if (data) {
              /*
              Our concession data will look like this
              // it will also have peopleCount
              [
                {
                  "queueTime": 10,
                  "peopleCount": 10,
                  "sensorActive": True,
                  "section": 113
                },
                {
                  "queueTime": 4,
                  "peopleCount": 10,
                  "sensorActive": True,
                  "section": 141
                }
              ]
              */
              const activeCount = data.filter(sensor => sensor.sensorActive).length;
              const totalSections = data.length;
              // const isOutage = activeCount / totalCount <= 0.5;
              const currentTotalQueueTime = data.reduce((x, y) => x + y.queueTime, 0);
              const currentTotalPeopleCount = data.reduce((x, y) => x + y.peopleCount, 0);
              
              // Better to calculate avg with total wait times / active sensor counts
              const averageQueueTime = Math.round(activeCount ? currentTotalQueueTime / activeCount : 0);
              const averagePeopleCount = Math.round(activeCount ? currentTotalPeopleCount / activeCount : 0);
              
              const sectionQueueTimes = {};
              const sectionPeopleCounts = {};
              
              data.forEach(sensor => {
                sectionQueueTimes[sensor.section] = sensor.queueTime;
                sectionPeopleCounts[sensor.section] = sensor.peopleCount;
              });
  
              // Determine the data type based on sensor configuration
              const isCountMode = location.sensorDataType === "count";
  
              resolve({
                locationId: location.id,
                type: 'concession',
                data: {
                  type: isCountMode ? 'people count' : 'queue time',
                  // showOutage: isOutage,
                  averageQueueTime,
                  averagePeopleCount,
                  sectionQueueTimes,
                  sectionPeopleCounts,
                  activeSections: activeCount,
                  totalSections
                }
              });
            } else {
              resolve({ error: "invalid" });
            }
          },
          authCtx.token
        );
      });
    } else {
      return new Promise((resolve) => {
        getConcessionAPI(location)(
          "get-concession-location-current-data-dashboard-v2",
          {
            sections: location.sections.map(section => section.sectionId),
            venue: authCtx.venue, // phillies
            locationId: location.id, // 2 (concession)
            measurement: location.measurement || "" // clubhouse
          },
          (data, error) => {
            if (error) {
              if (error.status === 401) {
                authCtx.logout();
                navigate("/auth");
                resolve({ error: "unauthorized" });
              } else {
                resolve({ error: "failed" });
              }
            } else if (data) {
              /*
              Our concession data will look like this
              // it will also have peopleCount
              [
                {
                  "queueTime": 10,
                  "peopleCount": 10,
                  "sensorActive": True,
                  "section": 113
                },
                {
                  "queueTime": 4,
                  "peopleCount": 10,
                  "sensorActive": True,
                  "section": 141
                }
              ]
              */
              const activeCount = data.filter(sensor => sensor.sensorActive).length;
              const totalSections = data.length;
              // const isOutage = activeCount / totalCount <= 0.5;
              const currentTotalQueueTime = data.reduce((x, y) => x + y.queueTime, 0);
              const currentTotalPeopleCount = data.reduce((x, y) => x + y.peopleCount, 0);
              
              // Better to calculate avg with total wait times / active sensor counts
              const averageQueueTime = Math.round(activeCount ? currentTotalQueueTime / activeCount : 0);
              const averagePeopleCount = Math.round(activeCount ? currentTotalPeopleCount / activeCount : 0);
              
              const sectionQueueTimes = {};
              const sectionPeopleCounts = {};
              
              data.forEach(sensor => {
                sectionQueueTimes[sensor.section] = sensor.queueTime;
                sectionPeopleCounts[sensor.section] = sensor.peopleCount;
              });
  
              // Determine the data type based on sensor configuration
              const isCountMode = location.sensorDataType === "count";
  
              resolve({
                locationId: location.id,
                type: 'concession',
                data: {
                  type: isCountMode ? 'people count' : 'queue time',
                  // showOutage: isOutage,
                  averageQueueTime,
                  averagePeopleCount,
                  sectionQueueTimes,
                  sectionPeopleCounts,
                  activeSections: activeCount,
                  totalSections
                }
              });
            } else {
              resolve({ error: "invalid" });
            }
          },
          authCtx.token
        );
      });
    }
  };

  // Current data for a restrooms location (v2): each restroom's entries today and its latest queue
  // ([{ restroomId, sectionId, entries, exits, queueTime, queueCount, sensorActive }])
  const fetchFacilitiesLocationData = (location) => {
    return new Promise((resolve) => {
      getFacilitiesAPI(location)(
        "get-facilities-restrooms-location-current-data-dashboard-v2",
        {
          restrooms: location.restrooms || [],
          venue: authCtx.venue,
          locationId: location.id,
          measurement: location.measurement || "",
          queueMeasurement: location.queueMeasurement || "",
          // The route counts entries over the game day (same window as gates), worked out in the venue's time zone
          timezone: location.timeZone
        },
        (data, error) => {
          if (error) {
            if (error.status === 401) {
              authCtx.logout();
              navigate("/auth");
              resolve({ error: "unauthorized" });
            } else {
              resolve({ error: "failed" });
            }
          } else if (Array.isArray(data)) {
            resolve({
              locationId: location.id,
              type: 'facilities',
              data: { type: 'restrooms', restrooms: data }
            });
          } else {
            resolve({ error: "invalid" });
          }
        },
        authCtx.token
      );
    });
  };

  // Replace the existing fetchGatesLocationData function in LocationsController.js
  // Only change: adds totalEntrances to the resolve

  const fetchGatesLocationData = (location) => {
    return new Promise((resolve) => {
      // Same as getQueuesAPI unless gates dummy data mode is on (local dev only)
      getGatesAPI(location)(
        "get-gates-location-current-data-dashboard",
        {
          gates: location.gates.map(gate => ({
            ...gate,
            entranceSensorIds: gate.multiSensor?.queue_time?.components?.ema_rate?.sensorIds || []
          })),
          venue: authCtx.venue,
          locationId: location.id,
          measurement: location.measurement || "",
          queueMeasurement: location.queueMeasurement || "",
          timezone: location.timeZone
        },
        (data, error) => {
          if (error) {
            if (error.status === 401) {
              authCtx.logout();
              navigate("/auth");
              resolve({ error: "unauthorized" });
            } else {
              resolve({ error: "failed" });
            }
          } else if (data) {
            const totalExits = data.reduce((sum, gate) => sum + gate.totalExits, 0);
            const totalEntrances = data.reduce((sum, gate) => sum + (gate.totalEntrances || 0), 0);
            
            const maxQueueTime = Math.max(...data.map(gate => gate.queueTime || 0), 0);
            
            const gateQueueTimes = {};
            data.forEach(gate => {
              gateQueueTimes[gate.gateId] = {
                queueTime: gate.queueTime || 0,
                queueCount: gate.queueCount || 0
              };
            });

            resolve({
              locationId: location.id,
              type: 'gates',
              data: {
                type: 'counts',
                totalExits,
                totalEntrances,
                maxQueueTime,
                gateQueueTimes
              }
            });
          } else {
            resolve({ error: "invalid" });
          }
        },
        authCtx.token
      );
    });
  };


  // Live numbers for every location card. A background refresh (every 60s) keeps the cards'
  // previous numbers for any location whose request fails, instead of blanking them.
  const fetchLiveData = async (locationData, isBackground = false) => {
    const retailLocations = locationData.retail;
    const concessionLocations = locationData.concessions;
    const facilitiesLocations = locationData.facilities;
    const gatesLocations = locationData.gates;
    // Eventually we will do primses for concession concessionLocations.map etc
    const retailPromises = retailLocations.map(location => fetchRetailLocationData(location));
    const concessionsPromises = concessionLocations.map(location => fetchConcessionLocationData(location));
    const facilitiesPromises = facilitiesLocations.map(location => fetchFacilitiesLocationData(location));
    const gatesPromises = gatesLocations.map(location => fetchGatesLocationData(location));

    const retailConcessionsFacilitiesGatesPromises = [...retailPromises, ...concessionsPromises, ...facilitiesPromises, ...gatesPromises];
    const results = await Promise.all(retailConcessionsFacilitiesGatesPromises);
    // const results = await Promise.all(retailPromises);

    const newRetailData = {};
    const newConcessionData = {};
    const newFacilitiesData = {};
    const newGatesData = {};

    let anyOutage = false;

    results.forEach(result => {
      if (result.error) {
        console.error(`Error fetching data for location ${result.locationId}:`, result.error);
        return;
      }

      if (result.type === 'retail') {
        newRetailData[result.locationId] = result.data;
        if (result.data.showOutage) {
          anyOutage = true;
        }
      } else if (result.type === 'concession') {
        newConcessionData[result.locationId] = result.data;
        // if (result.data.showOutage) {
        //   anyOutage = true;
        // }
      } else if (result.type === 'facilities') {
        newFacilitiesData[result.locationId] = result.data;
        // if (result.data.showOutage) {
        //   anyOutage = true;
        // }
      } else if (result.type === 'gates') {
        newGatesData[result.locationId] = result.data;
        // if (result.data.showOutage) {
        //   anyOutage = true;
        // }
      }
    });

    const merge = (next) => (prev) => (isBackground ? { ...prev, ...next } : next);
    setRetailLocationData(merge(newRetailData));
    setConcessionLocationData(merge(newConcessionData));
    setFacilitiesLocationData(merge(newFacilitiesData));
    setGatesLocationData(merge(newGatesData));
    setShowOutageAlert(anyOutage);
  };

  const fetchData = async () => {
    setIsLoading(true);

    try {
      const refreshResponse = await new Promise((resolve, reject) => {
        getQueuesAPI(
          "refresh-user-auth-data", 
          { venue: authCtx.venue },
          (data, error) => {
            if (error) {
              reject(error);
            } else {
              resolve(data);
            }
            // console.log("REFRESH DATA RECEIEVED")
          },
          authCtx.token
        );
      });
  
      // Update localStorage and context with new auth data
      if (refreshResponse) {
        // console.log('REFRESHED WITH UP TO DATE AUTH DATA:')
        // console.log(refreshResponse)
        // console.log('User data refreshed...')
        authCtx.updateAuthLocations(refreshResponse.authLocations);
        authCtx.updateAuthLocationData(refreshResponse.authLocationData);
        authCtx.updateDashboardEnabled(refreshResponse.dashboardEnabled);
      }

      // LocationData is only data from authLocationData which is updated/refreshed using the above route.
      // Backend correctly determins the users authLocations and uses it to correctly return the authLocationData
      // Dont think we use authLocations just storing it for now...
      const locationData = authCtx.authLocationData;

      setRetail(locationData.retail);
      setOriginalRetail(locationData.retail);
      setConcessions(locationData.concessions);
      setOriginalConcessions(locationData.concessions);
      setFacilities(locationData.facilities);
      setOriginalFacilities(locationData.facilities);
      setGates(locationData.gates);
      setOriginalGates(locationData.gates);

      await fetchLiveData(locationData);
    } catch (error) {
      // console.error("Error fetching retail data:", error);
      if (error && error.status === 401) {
        authCtx.logout();
        navigate("/auth");
      } else {
        alert("An error occurred while fetching data. Please refresh the page and try again.");
      }
    } finally {
      setLastUpdatedAt(Date.now());
      setIsLoading(false);
    }
  };

  const handleSearch = (value) => {
    setCurrentSearchTerm(value);
    const searchTerm = value.toLowerCase();

    const filteredConcessions = originalConcessions.filter(concession =>
      concession.name.toLowerCase().includes(searchTerm)
    );

    const filteredRetail = originalRetail.filter(retail =>
      retail.name.toLowerCase().includes(searchTerm)
    );

    setRetail(filteredRetail);
    setConcessions(filteredConcessions);
  };

  const handleSort = (option, direction) => {
    const sortValue = `${option}-${direction}`;
    setSortConfig(sortValue);

    const sortRetailItems = (items) => {
      return [...items].sort((a, b) => {
        switch (option) {
          case 'name':
            const nameCompare = a.name.localeCompare(b.name);
            return direction === "asc" ? nameCompare : -nameCompare;
          case 'occupancy':
            const dataA = retailLocationData[a.id];
            const dataB = retailLocationData[b.id];
            const valueA = dataA?.type === 'occupancy' ? dataA.percentage : (dataA?.count || 0);
            const valueB = dataB?.type === 'occupancy' ? dataB.percentage : (dataB?.count || 0);
            const compareValue = valueA - valueB;
            return direction === "asc" ? compareValue : -compareValue;
          default:
            return 0;
        }
      });
    };
    // TODO Fix this to compare average wait time
    const sortConcessionItems = (items) => {
      return [...items].sort((a, b) => {
        switch (option) {
          case 'name':
            const nameCompare = a.name.localeCompare(b.name);
            return direction === "asc" ? nameCompare : -nameCompare;
          case 'occupancy':
            const dataA = concessionLocationData[a.id];
            const dataB = concessionLocationData[b.id];
            
            // Use appropriate metric based on sensor data type
            let valueA = 0;
            let valueB = 0;
            
            if (dataA) {
              valueA = dataA.type === 'people count' ? dataA.averagePeopleCount : dataA.averageQueueTime;
            }
            if (dataB) {
              valueB = dataB.type === 'people count' ? dataB.averagePeopleCount : dataB.averageQueueTime;
            }
            
            const compareValue = valueA - valueB;
            return direction === "asc" ? compareValue : -compareValue;
          default:
            return 0;
        }
      });
    };

    setRetail(sortRetailItems(retail));
    setConcessions(sortConcessionItems(concessions));
  };

  return children({
    isLoading,
    retail,
    concessions,
    facilities,
    gates,
    retailLocationData,
    concessionLocationData,
    facilitiesLocationData,
    gatesLocationData,
    showOutageAlert,
    showFoodSection,
    showBeverageSection,
    showRetailSection,
    showFacilitiesSection,
    showGatesSection,
    foodItems,
    beverageItems,
    authCtx,
    handleSearch,
    handleSort,
    handleDismiss,
    refreshLocations: fetchData,
    // Live refresh status for the header
    lastUpdatedAt,
    nextRefreshAt,
    isRefreshing,
    refreshNow: refreshLiveData
  });
};

export default LocationsController;